

import { useMemo, useState } from 'react';
import './CarbFinder.css';

const FINDER_OPTIONS = {
  application: ['Marine', 'Hot Rod', 'Jeep', 'Toyota', 'Classic Car', 'Performance'],
  brand: ['Rochester', 'Holley', 'Carter', 'Stromberg', 'Aisan', 'Weber'],
  barrel: ['One Barrel', 'Two Barrel', 'Four Barrel'],
  choke: ['Manual', 'Electric', 'Hot Air', 'None / Unknown'],
};

const RECOMMENDATIONS = [
  {
    id: 'marine-rochester-qjet',
    application: 'Marine',
    brand: 'Rochester',
    barrel: 'Four Barrel',
    choke: 'Electric',
    title: 'Rochester Marine Quadrajet',
    categorySlug: 'rochester_marine_quadrajet',
    summary: 'A strong match for marine applications that need dependable four-barrel Rochester performance.',
  },
  {
    id: 'marine-holley-2bbl',
    application: 'Marine',
    brand: 'Holley',
    barrel: 'Two Barrel',
    choke: 'Electric',
    title: 'Holley Marine Carburetor Two Barrel',
    categorySlug: 'holley_marine_carburetor_two_barrel',
    summary: 'A solid two-barrel Holley option for marine engines needing stable fuel delivery.',
  },
  {
    id: 'hotrod-holley-4160',
    application: 'Hot Rod',
    brand: 'Holley',
    barrel: 'Four Barrel',
    choke: 'Electric',
    title: 'Holley Four Barrel 4160',
    categorySlug: 'holley_four_barrel_4160',
    summary: 'A classic hot rod favorite built around four-barrel performance and tunability.',
  },
  {
    id: 'jeep-carter-bbd',
    application: 'Jeep',
    brand: 'Carter',
    barrel: 'Two Barrel',
    choke: 'Electric',
    title: 'Carter BBD Carburetor Jeep',
    categorySlug: 'carter_bbd_carburetor_jeep',
    summary: 'A Jeep-oriented Carter BBD recommendation for utility, drivability, and fitment familiarity.',
  },
  {
    id: 'classic-stromberg97',
    application: 'Classic Car',
    brand: 'Stromberg',
    barrel: 'Two Barrel',
    choke: 'Manual',
    title: 'Stromberg 97',
    categorySlug: 'stromberg97',
    summary: 'An iconic classic-carb recommendation for vintage builds and nostalgic performance.',
  },
  {
    id: 'toyota-aisan',
    application: 'Toyota',
    brand: 'Aisan',
    barrel: 'Two Barrel',
    choke: 'Electric',
    title: 'Aisan',
    categorySlug: 'aisan',
    summary: 'A practical fit for Toyota-related carburetor searches where Aisan is the likely family.',
  },
  {
    id: 'performance-rochester-qjet',
    application: 'Performance',
    brand: 'Rochester',
    barrel: 'Four Barrel',
    choke: 'Electric',
    title: 'Rochester Performance Quadrajet Carburetor',
    categorySlug: 'rochester_performance_quadrajet_carburetor',
    summary: 'A strong performance-oriented Rochester match for four-barrel setups and tuning work.',
  },
];

function CarbFinder() {
  const [formData, setFormData] = useState({
    application: '',
    brand: '',
    barrel: '',
    choke: '',
  });

  const handleChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const matches = useMemo(() => {
    return RECOMMENDATIONS.filter((item) => {
      return (
        (!formData.application || item.application === formData.application) &&
        (!formData.brand || item.brand === formData.brand) &&
        (!formData.barrel || item.barrel === formData.barrel) &&
        (!formData.choke || item.choke === formData.choke)
      );
    });
  }, [formData]);

  const hasSelections = Object.values(formData).some(Boolean);

  return (
    <div className="carb-finder-page">
      <section className="carb-finder-hero">
        <div className="carb-finder-hero__copy">
          <span className="carb-finder-eyebrow">Carb Finder</span>
          <h1>Find the carburetor family that best matches your setup.</h1>
          <p>
            Start with the application, brand, barrel type, and choke style. This finder gives you a smart shortlist
            that can later connect directly to your Odoo products and category pages.
          </p>
        </div>
        <div className="carb-finder-hero__stats">
          <div className="carb-finder-stat-card">
            <strong>Fast filtering</strong>
            <span>Application-based matching</span>
          </div>
          <div className="carb-finder-stat-card">
            <strong>Sidebar ready</strong>
            <span>Connects to product categories</span>
          </div>
          <div className="carb-finder-stat-card">
            <strong>Odoo ready</strong>
            <span>Prepared for live inventory later</span>
          </div>
        </div>
      </section>

      <section className="carb-finder-layout">
        <div className="carb-finder-panel">
          <div className="carb-finder-panel__header">
            <h2>Filter Your Search</h2>
            <p>Choose the details you know. The finder narrows down likely carburetor families.</p>
          </div>

          <div className="carb-finder-grid">
            <label className="carb-finder-field">
              <span>Application</span>
              <select value={formData.application} onChange={(event) => handleChange('application', event.target.value)}>
                <option value="">Select application</option>
                {FINDER_OPTIONS.application.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className="carb-finder-field">
              <span>Brand</span>
              <select value={formData.brand} onChange={(event) => handleChange('brand', event.target.value)}>
                <option value="">Select brand</option>
                {FINDER_OPTIONS.brand.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className="carb-finder-field">
              <span>Barrel Type</span>
              <select value={formData.barrel} onChange={(event) => handleChange('barrel', event.target.value)}>
                <option value="">Select barrel type</option>
                {FINDER_OPTIONS.barrel.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className="carb-finder-field">
              <span>Choke Style</span>
              <select value={formData.choke} onChange={(event) => handleChange('choke', event.target.value)}>
                <option value="">Select choke</option>
                {FINDER_OPTIONS.choke.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="carb-finder-actions">
            <button
              type="button"
              className="carb-finder-button carb-finder-button--secondary"
              onClick={() =>
                setFormData({
                  application: '',
                  brand: '',
                  barrel: '',
                  choke: '',
                })
              }
            >
              Reset Filters
            </button>
            <a href="/products" className="carb-finder-button carb-finder-button--primary">
              Browse All Products
            </a>
          </div>
        </div>

        <div className="carb-finder-results">
          <div className="carb-finder-results__header">
            <h2>Suggested Matches</h2>
            <p>
              {hasSelections
                ? `${matches.length} match${matches.length === 1 ? '' : 'es'} found based on your current selections.`
                : 'Choose some filters to narrow down the best carburetor families for your setup.'}
            </p>
          </div>

          <div className="carb-finder-results__list">
            {matches.length > 0 ? (
              matches.map((match) => (
                <article key={match.id} className="carb-finder-card">
                  <div className="carb-finder-card__top">
                    <span className="carb-finder-card__badge">{match.application}</span>
                    <span className="carb-finder-card__meta">
                      {match.brand} · {match.barrel}
                    </span>
                  </div>
                  <h3>{match.title}</h3>
                  <p>{match.summary}</p>
                  <div className="carb-finder-card__footer">
                    <span>Choke: {match.choke}</span>
                    <a href={`/products#${match.categorySlug}`}>View Category</a>
                  </div>
                </article>
              ))
            ) : (
              <div className="carb-finder-empty-state">
                <h3>No exact matches yet</h3>
                <p>
                  Try loosening one filter, or browse all categories manually until the live product inventory is wired in.
                </p>
                <a href="/products">Go to Products</a>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CarbFinder;